import React from 'react';
import { motion } from 'framer-motion';
import {
  Facebook,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../assets/TACOLOGO4.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/thetacogarage/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/the_taco_garage/', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@TacoGarage', label: 'YouTube' }
  ];

  const contactInfo = [
    { icon: Mail, text: 'support@thetacogarage.com', href: 'mailto:support@thetacogarage.com' },
    { icon: Phone, text: '+1 619-693-8907', href: 'tel:+16196938907' },
    { icon: MapPin, text: '1409 Broadway, Suite 101, Chula Vista CA 91911', href: '#' }
  ];

  return (
    <footer className="bg-gradient-to-b from-charcoal to-[#0E0E0E] border-t border-darkgray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Grid Section */}
        <div className="py-10 sm:py-16">
          <div className="grid grid-cols-1 gap-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-6">

            {/* 1️⃣ Brand */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="mb-4">
                  <Link to="/">
                    <img
                      src={logo}
                      alt="Taco Garage Logo"
                      className="h-16 w-auto object-contain"
                    />
                  </Link>
                </div>
                <p className="text-offwhite/70 mb-6 max-w-[250px]">
                  Your premier destination for Toyota truck parts and accessories. 
                  Quality products, expert support, and a community of enthusiasts.
                </p>
                <div className="space-y-3">
                  {contactInfo.map((contact, index) => (
                    <motion.a
                      key={index}
                      href={contact.href}
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-3 text-offwhite/70 hover:text-brandred transition-colors duration-300"
                    >
                      <contact.icon size={16} />
                      <span className="text-sm">{contact.text}</span>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* 2️⃣ Products */}
            <div>
              <h4 className="text-lg font-semibold text-offwhite mb-4">Products</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/shop?category=top-sellers"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Top Sellers
                  </Link>
                </li>
                <li>
                  <Link
                    to="/shop?category=lighting"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Lighting
                  </Link>
                </li>
                <li>
                  <Link
                    to="/shop?category=suspension"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Suspension
                  </Link>
                </li>
                <li>
                  <Link
                    to="/shop?category=armor"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Armor
                  </Link>
                </li>
                <li>
                  <Link
                    to="/shop?category=accessories"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Accessories
                  </Link>
                </li>
              </ul>
            </div>

            {/* 3️⃣ Support */}
            <div>
              <h4 className="text-lg font-semibold text-offwhite mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/contact"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact?inquiry=installation"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Installation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact?inquiry=returns"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Returns
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>

            {/* 4️⃣ Company */}
            <div>
              <h4 className="text-lg font-semibold text-offwhite mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/shop-location"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Shop Location
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact?inquiry=installation"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Installation Services
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* 5️⃣ Legal */}
            <div>
              <h4 className="text-lg font-semibold text-offwhite mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookie-policy"
                    className="text-offwhite/70 hover:text-brandred transition-colors duration-300 text-sm"
                  >
                    Cookie Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ✉️ Newsletter Signup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="py-6 sm:py-8 border-t border-darkgray"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center sm:gap-6">
            <div>
              <h4 className="text-lg font-semibold text-offwhite mb-2">Stay Updated</h4>
              <p className="text-offwhite/70 text-sm">
                Get the latest product updates and exclusive offers
              </p>
            </div>
            <form
              method="post"
              action="https://thetcogarage.myshopify.com/contact#contact_form"
              acceptCharset="UTF-8"
              className="flex flex-col gap-3 sm:flex-row sm:gap-4 mt-3 sm:mt-0 w-full sm:w-auto"
            >
              <input type="hidden" name="form_type" value="customer" />
              <input type="hidden" name="utf8" value="✓" />
              <input
                type="email"
                name="contact[email]"
                placeholder="Enter your email"
                required
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandred focus:border-transparent transition-all duration-300"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="bg-brandred font-bold px-6 py-2 rounded-lg shadow-lg hover:bg-gradient-to-r hover:from-brandred hover:to-[#E63B3B] transition-all duration-300"
              >
                Subscribe
              </motion.button>
            </form>
          </div>
        </motion.div>

        {/* ⚙️ Bottom Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="py-6 sm:py-8 border-t border-darkgray"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center sm:gap-6">
            <div className="flex items-center gap-2 text-offwhite/50 text-sm">
              <span>© {currentYear} The Taco Garage. All rights reserved.</span>
              <span>•</span>
              <span>Made with</span>
              <Heart size={14} className="text-brandred fill-current" />
              <span>for Toyota enthusiasts</span>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-2 sm:gap-4 mt-2 sm:mt-0">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 bg-darkgray border border-white rounded-lg flex items-center justify-center text-offwhite/70 hover:text-brandred hover:border-brandred transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>

            {/* Payments */}
            <div className="flex items-center gap-2 text-offwhite/50 text-xs">
              <span>Secure payments:</span>
              <div className="flex gap-1">
                <span>💳</span>
                <span>🔒</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
